import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, MapPin, Calendar, TreePine, Heart, ChevronDown, Settings, LogOut, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccountSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [stats, setStats] = useState({ memorials: 0, trees: 0 });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [userRes, memorialsRes, treesRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/memorials/my`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/family-trees`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setUser(userRes.data);
      setStats({
        memorials: memorialsRes.data?.length || 0,
        trees: treesRes.data?.length || 0
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/memorial-tribute')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/family-tree')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
              <span className="text-sm font-sans text-foreground hidden sm:block">{user.name}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
            
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Account Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/home')}
          className="flex items-center text-foreground-muted hover:text-primary mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>

        {/* Page Title */}
        <h1 className="text-4xl font-serif text-foreground mb-8">Account Settings</h1>

        {/* Profile Card */}
        <div className="bg-white border border-border shadow-card rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8 border-b border-border">
            <div className="flex items-center gap-6">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg"
                  data-testid="user-profile-picture"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center border-4 border-white shadow-lg">
                  <User className="w-12 h-12 text-primary" />
                </div>
              )}
              <div>
                <h2 className="text-3xl font-serif text-foreground" data-testid="user-name">{user.name}</h2>
                <p className="text-foreground-muted font-sans">{user.role === 'poc' ? 'Family Admin' : 'Member'}</p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-6">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-sans text-foreground-muted">Email Address</p>
                <p className="text-lg font-sans text-foreground" data-testid="user-email">{user.email}</p>
              </div>
            </div>

            {/* Account Created */}
            {user.created_at && (
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-sans text-foreground-muted">Member Since</p>
                  <p className="text-lg font-sans text-foreground">
                    {new Date(user.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-border shadow-card rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-rose-500" />
              </div>
              <div>
                <p className="text-3xl font-serif text-foreground" data-testid="memorial-count">{stats.memorials}</p>
                <p className="text-sm font-sans text-foreground-muted">Memorial Tributes Created</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-border shadow-card rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <TreePine className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-3xl font-serif text-foreground" data-testid="tree-count">{stats.trees}</p>
                <p className="text-sm font-sans text-foreground-muted">Family Trees Created</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white border border-border shadow-card rounded-xl p-8">
          <h3 className="text-xl font-serif text-foreground mb-6">Quick Actions</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Button
              onClick={() => navigate('/create-memorial')}
              variant="outline"
              className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-lg px-6 py-4 font-sans justify-start"
            >
              <Heart className="w-5 h-5 mr-3" />
              Create New Memorial
            </Button>
            <Button
              onClick={() => navigate('/tree-builder')}
              variant="outline"
              className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-lg px-6 py-4 font-sans justify-start"
            >
              <TreePine className="w-5 h-5 mr-3" />
              Build Family Tree
            </Button>
          </div>
        </div>

        {/* Sign Out Button */}
        <div className="mt-8 pt-8 border-t border-border">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-6 py-3"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 bg-paper border-t border-border mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <TreePine className="w-6 h-6 text-primary" strokeWidth={1.5} />
              <span className="text-xl font-serif font-semibold text-foreground">Sarmoh</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/terms" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center pt-6 border-t border-border">
            <p className="text-sm font-sans text-muted-foreground">
              © 2026 TLML Mediatech Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
